import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaUsers, FaTimesCircle } from 'react-icons/fa';
import { usersService } from '../../services/supabase/users.service';
import { User } from '../../types/user';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';

const SelectorContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-right: 1rem;
`;

const SelectorButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: ${({ theme, $isActive }) => $isActive ? theme.colors.primary : theme.colors.backgroundLight};
  color: ${({ theme, $isActive }) => $isActive ? 'white' : theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme, $isActive }) => $isActive ? theme.colors.primaryDark : theme.colors.border};
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  width: 250px;
  max-height: 300px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
  margin-top: 0.5rem;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
  
  &.active {
    background-color: ${({ theme }) => theme.colors.primaryLight};
    font-weight: 500;
  }
`;

const UserAvatar = styled.div<{ $imageUrl?: string | null; $borderColor?: string | null; $initials?: string }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${({ theme, $imageUrl, $borderColor }) => $imageUrl ? 'transparent' : ($borderColor || theme.colors.primary)};
  background-image: ${({ $imageUrl }) => $imageUrl ? `url(${$imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  border: 2px solid ${({ $borderColor, theme }) => $borderColor || theme.colors.primary};
  position: relative;
  overflow: visible;
  z-index: 1;
  
  &::after {
    content: ${({ $initials }) => $initials ? `"${$initials}"` : '""'};
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    z-index: 2;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.div`
  font-size: 0.9rem;
`;

const UserRole = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textLight};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-bottom-color: ${({ theme }) => theme.colors.primary};
  }
`;

const NoResults = styled.div`
  padding: 1rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 0.9rem;
`;

const ActiveFilterBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  border: 2px solid white;
`;

const ClearFilterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.danger};
  cursor: pointer;
  padding: 0.25rem;
  margin-left: 0.5rem;
  
  &:hover {
    color: ${({ theme }) => theme.colors.dangerDark};
  }
`;

interface UserSelectorProps {
  onSelectUser: (userId: string | null) => void;
  selectedUserId: string | null;
  inlineStyle?: boolean; // Indique si le sélecteur doit être affiché en ligne (dans un menu déroulant)
}

const UserSelector: React.FC<UserSelectorProps> = ({ onSelectUser, selectedUserId, inlineStyle = false }) => {
  // Log pour le débogage
  console.log('🔍 UserSelector rendu avec:', { selectedUserId, inlineStyle });
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [hasMultipleUsers, setHasMultipleUsers] = useState(false);
  const { supabaseUser: _supabaseUser } = useAuth();
  const { addNotification } = useNotifications();
  
  // Récupérer la liste des utilisateurs
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const { data, error } = await usersService.getUsersByAgency();
        
        if (error) {
          throw error;
        }
        
        const usersList = data || [];
        setUsers(usersList);
        
        // Vérifier s'il y a plus d'un utilisateur dans l'agence
        setHasMultipleUsers(usersList.length > 1);
        
        // Si un utilisateur est déjà sélectionné, trouver ses informations
        if (selectedUserId) {
          const user = usersList.find(u => u.id === selectedUserId) || null;
          setSelectedUser(user);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des utilisateurs:', error);
        addNotification({
          type: 'warning',
          title: 'Erreur',
          message: 'Impossible de récupérer la liste des utilisateurs'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, [selectedUserId, addNotification]);
  
  // Filtrer les utilisateurs en fonction du terme de recherche
  const filteredUsers = searchTerm.trim() === '' 
    ? users 
    : users.filter(user => 
        user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
  
  // Gérer la sélection d'un utilisateur
  const handleSelectUser = (user: User) => {
    setSelectedUser(user);
    onSelectUser(user.id);
    setIsOpen(false);
  };
  
  // Gérer la réinitialisation du filtre
  const handleClearFilter = () => {
    setSelectedUser(null);
    
    // Si plusieurs utilisateurs, on peut sélectionner "Tous les utilisateurs"
    // Sinon, on sélectionne le seul utilisateur disponible (l'admin lui-même)
    if (hasMultipleUsers) {
      onSelectUser(null);
    } else if (users.length === 1) {
      // S'il n'y a qu'un seul utilisateur, on le sélectionne automatiquement
      handleSelectUser(users[0]);
    }
  };
  
  // Extraire l'initiale du nom d'utilisateur pour l'avatar
  const getUserInitial = (user: User) => {
    return user.first_name && user.name
      ? `${user.first_name[0]}${user.name[0]}`
      : user.email
        ? user.email[0].toUpperCase()
        : 'U';
  };
  
  // Fermer le menu lorsqu'on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-selector')) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Au chargement initial, si l'utilisateur est seul et que selectedUserId est null,
  // sélectionner automatiquement l'utilisateur actuel
  useEffect(() => {
    if (!hasMultipleUsers && users.length === 1 && selectedUserId === null) {
      // On attend un peu pour s'assurer que le composant parent est prêt à recevoir l'événement
      const timer = setTimeout(() => {
        handleSelectUser(users[0]);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [hasMultipleUsers, users, selectedUserId]);

  // Si le sélecteur est affiché en ligne (dans un menu déroulant)
  if (inlineStyle) {
    return (
      <div className="user-selector-inline">
        {/* Afficher un titre pour le sélecteur en ligne */}
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #eee', fontSize: '0.9rem', fontWeight: 500 }}>
          {selectedUser ? 'Utilisateur sélectionné' : 'Sélectionner un utilisateur'}
          {selectedUser && (
            <button 
              onClick={handleClearFilter}
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                color: '#f44336',
                float: 'right',
                padding: '0',
                fontSize: '0.8rem'
              }}
            >
              Réinitialiser
            </button>
          )}
        </div>
        
        {/* Barre de recherche */}
        <SearchInput 
          type="text" 
          placeholder="Rechercher un utilisateur..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ margin: '8px' }}
        />
        
        {/* Liste des utilisateurs */}
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {loading ? (
            <NoResults>Chargement des utilisateurs...</NoResults>
          ) : filteredUsers.length === 0 ? (
            <NoResults>Aucun utilisateur trouvé</NoResults>
          ) : (
            <>
              {/* Option pour afficher tous les utilisateurs - uniquement si plusieurs utilisateurs existent */}
              {hasMultipleUsers && (
                <MenuItem 
                  key="all-users" 
                  onClick={() => onSelectUser(null)}
                  className={selectedUserId === null ? 'active' : ''}
                  style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}
                >
                  <div style={{ 
                    width: '30px', 
                    height: '30px', 
                    borderRadius: '50%', 
                    backgroundColor: '#4285F4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}>
                    <FaUsers size={16} />
                  </div>
                  <UserInfo>
                    <UserName>Tous les utilisateurs</UserName>
                    <UserRole>Afficher tous les marqueurs</UserRole>
                  </UserInfo>
                </MenuItem>
              )}
              
              {/* Liste des utilisateurs individuels */}
              {filteredUsers.map(user => (
                <MenuItem 
                  key={user.id} 
                  onClick={() => handleSelectUser(user)}
                  className={selectedUserId === user.id ? 'active' : ''}
                  style={{ padding: '8px 12px' }}
                >
                  <UserAvatar 
                    $imageUrl={user.photo_url}
                    $borderColor={user.color || undefined}
                    $initials={!user.photo_url ? getUserInitial(user) : undefined}
                  />
                  <UserInfo>
                    <UserName>{user.first_name} {user.name}</UserName>
                    <UserRole>{user.role === 'admin' ? 'Administrateur' : 'Utilisateur'}</UserRole>
                  </UserInfo>
                </MenuItem>
              ))}
            </>
          )}
        </div>
      </div>
    );
  }
  
  // Affichage standard (bouton flottant)
  console.log('🔍 UserSelector - Avant rendu, selectedUser:', selectedUser, 'selectedUserId:', selectedUserId);
  return (
    <SelectorContainer className="user-selector">
      <SelectorButton 
        onClick={() => setIsOpen(!isOpen)} 
        $isActive={!!selectedUser}
      >
        {selectedUser ? (
          <>
            <UserAvatar 
              $imageUrl={selectedUser.photo_url}
              $borderColor={selectedUser.color || undefined}
              $initials={!selectedUser.photo_url ? getUserInitial(selectedUser) : undefined}
            />
            <span>{selectedUser.first_name || 'Utilisateur'}</span>
          </>
        ) : (
          <>
            <div style={{ 
              width: '30px', 
              height: '30px', 
              borderRadius: '50%', 
              backgroundColor: '#4285F4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}>
              <FaUsers size={16} />
            </div>
            <span>Tous les utilisateurs</span>
          </>
        )}
        {selectedUser && <ActiveFilterBadge />}
      </SelectorButton>
      
      {selectedUser && (
        <ClearFilterButton onClick={handleClearFilter} title="Effacer le filtre">
          <FaTimesCircle size={16} />
        </ClearFilterButton>
      )}
      
      <DropdownMenu $isOpen={isOpen}>
        <SearchInput 
          type="text" 
          placeholder="Rechercher un utilisateur..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        
        {loading ? (
          <NoResults>Chargement des utilisateurs...</NoResults>
        ) : filteredUsers.length === 0 ? (
          <NoResults>Aucun utilisateur trouvé</NoResults>
        ) : (
          <>
            {/* Option pour afficher tous les utilisateurs - uniquement si plusieurs utilisateurs existent */}
            {hasMultipleUsers && (
              <MenuItem 
                key="all-users" 
                onClick={() => onSelectUser(null)}
                className={selectedUserId === null ? 'active' : ''}
                style={{ borderBottom: '1px solid #eee' }}
              >
                <div style={{ 
                  width: '30px', 
                  height: '30px', 
                  borderRadius: '50%', 
                  backgroundColor: '#4285F4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <FaUsers size={16} />
                </div>
                <UserInfo>
                  <UserName>Tous les utilisateurs</UserName>
                  <UserRole>Afficher tous les marqueurs</UserRole>
                </UserInfo>
              </MenuItem>
            )}
            
            {/* Liste des utilisateurs individuels */}
            {filteredUsers.map(user => (
              <MenuItem 
                key={user.id} 
                onClick={() => handleSelectUser(user)}
                className={selectedUserId === user.id ? 'active' : ''}
              >
                <UserAvatar $imageUrl={user.photo_url}>
                  {!user.photo_url && getUserInitial(user)}
                </UserAvatar>
                <UserInfo>
                  <UserName>{user.first_name} {user.name}</UserName>
                  <UserRole>{user.role === 'admin' ? 'Administrateur' : 'Utilisateur'}</UserRole>
                </UserInfo>
              </MenuItem>
            ))}
          </>
        )}
      </DropdownMenu>
    </SelectorContainer>
  );
};

export default UserSelector;

